'use client';

import React, { useEffect, useState } from 'react';
import { ElPortalWordmark } from '../ElPortalWordmark';

interface AuthLayoutProps {
    children: React.ReactNode;
}

const StarParticles = ({ count }: { count: number }) => {
    const [particles, setParticles] = useState<{ top: string; left: string; opacity: number }[]>([]);

    useEffect(() => {
        const newParticles = [...Array(count)].map(() => ({
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            opacity: Math.random()
        }));
        setParticles(newParticles);
    }, [count]);

    if (particles.length === 0) return null;

    return (
        <div className="absolute inset-0 opacity-[0.05]">
            {particles.map((p, i) => (
                <div
                    key={i}
                    className="absolute w-px h-px bg-fg rounded-full"
                    style={{
                        top: p.top,
                        left: p.left,
                        opacity: p.opacity
                    }}
                />
            ))}
        </div>
    );
};

export const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
    return (
        <div data-auth-surface className="dark fixed inset-0 z-50 flex flex-col items-center justify-center bg-[var(--bg-well)] overflow-hidden font-sans select-none">

            {/* Cinematic Background Layers */}
            <div className="absolute inset-0">
                {/* Volumetric cobalt wash at the bottom edge, the surface's one sanctioned
                    ambient carve-out (design-system Motion, CONTEXT D-10).

                    OWNER DIRECTION 2026-08-06: this wash is COBALT, the El Portal brand blue.
                    It is built from the --data cobalt ramp (hue 264, the logo cobalt) rather
                    than from literals, so it cannot drift and needs no literal carve-out:
                      base  --data-default  oklch(56% .16 264) in Ceramic Night
                      bloom --data-hover    oklch(62% .16 264), one step lighter

                    Two deliberate deviations, both the owner's call, recorded so a later
                    sweep does not "correct" them:
                    1. design-system.md:283 scopes --data to charts and data, not chrome. An
                       ambient wash is neither chrome nor a chart, and the owner has ruled
                       that cobalt is brand. If that line is ever tightened, exempt this file.
                    2. design-system.md:203 calls the sanctioned auth atmosphere a "navy"
                       wash. There is no reliable navy token in Ceramic Night: the nominal
                       one, --primary-accent, resolves to warm bone here. That is exactly the
                       bug plan 08-02 shipped and 62f9e535 reverted. --data is genuinely
                       cobalt in BOTH themes, which is why it is the correct source.

                    Two layers, not one, because a single blurred ellipse reads flat. The wide
                    base carries the field; the tighter, brighter core gives it volume. The
                    core is deliberately narrower AND less blurred than the base, and that
                    contrast in falloff is what makes it read as depth rather than as fog.

                    MEASURED 2026-08-01, Tailwind 4.3.3: getComputedStyle reads
                    animationDuration 8s, iterationCount infinite. duration-8000 is
                    load-bearing (it sets --tw-duration, which the v4 animate-* utility
                    consumes as animation-duration), so UI-SPEC M-6 is refuted, not confirmed. */}
                <div className="absolute bottom-[-16%] left-1/2 -translate-x-1/2 w-[104%] h-[62vh] blur-[170px] rounded-[100%] opacity-100" style={{ backgroundColor: 'color-mix(in oklch, var(--data-default) 44%, transparent)' }}></div>
                <div className="absolute bottom-[-8%] left-1/2 -translate-x-1/2 w-[72%] h-[42vh] blur-[115px] rounded-[100%] animate-pulse duration-8000 transition-opacity" style={{ backgroundColor: 'color-mix(in oklch, var(--data-hover) 22%, transparent)' }}></div>

                {/* Global Film Grain Overlay */}
                <div
                    className="absolute inset-0 opacity-[0.09] mix-blend-overlay pointer-events-none"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
                    }}
                ></div>

                {/* Parallax Star Particles */}
                <StarParticles count={6} />
            </div>

            <div className="relative z-10 flex flex-col items-center w-full max-w-[440px] px-6">

                {/* Main card. A genuine floating surface over the atmosphere, which is the
                    one place backdrop-blur and a shadow are sanctioned. The 1px gradient
                    hairline wrapper and the specular glint that hovered on it were both
                    retired: the house hairline is one token, and container cards do not
                    react on hover (design-system, owner-locked 2026-07-24). */}
                <div className="relative w-full rounded-2xl border border-border bg-overlay/60 backdrop-blur-2xl overflow-hidden shadow-near">

                    {/* Dot grid across the whole card back.
                        MEASURED 2026-08-06: the previous spelling was invisible. It combined
                        opacity .1 with mix-blend-soft-light over a card interior measuring
                        rgb(33,31,28); soft-light against a near-black backdrop is close to a
                        no-op, and a horizontal scan across the card read min 9 / max 11, a
                        delta of 2 out of 255. It also only covered the top h-64, so even at a
                        visible strength it was a header texture, not a card background.
                        Now: inset-0 so it actually backs the card, no blend mode, and the dot
                        carries its own alpha instead of relying on element opacity.
                        The mix is `in srgb`, not `in oklch`: mixing a token toward
                        `transparent` in oklch makes the hue powerless, and the computed value
                        came back as `oklch(.955 .014 none / .26)`. The dot is neutral so srgb
                        is both correct and predictable here. The mask
                        fades it out toward the lower third so the form fields sit on a clean
                        surface and the texture reads as depth behind the wordmark. */}
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            backgroundImage: `radial-gradient(circle at 3px 3px, color-mix(in srgb, var(--foreground) 10%, transparent) 1px, transparent 0)`,
                            backgroundSize: '16px 16px',
                            WebkitMaskImage: 'linear-gradient(170deg, black 0%, black 22%, transparent 55%)',
                            maskImage: 'linear-gradient(170deg, black 0%, black 22%, transparent 55%)'
                        }}
                    ></div>

                    {/* Internal Rim Lighting (Precision Edge) */}
                    <div className="absolute inset-0 rounded-2xl border-t border-l border-border pointer-events-none"></div>

                    {/* Card Content Interior */}
                    <div className="relative p-6 sm:p-8 animate-in fade-in duration-700">

                        {/* Branding Wordmark (Sidebar Sync) */}
                        <div className="flex justify-center mb-10">
                            <ElPortalWordmark size={36} glow textClassName="text-fg" />
                        </div>

                        {/* Children Content */}
                        {children}

                    </div>
                </div>
            </div>
        </div>
    );
};
